<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'

import type { HTMLAttributeClass } from '@fondof-ui/types'
import { useBlockScroll } from '@internal/helper'
import { onClickOutside } from '@vueuse/core'

import { CloseCircleIcon } from '@fond-of/satch-icons'

const props = withDefaults(
  defineProps<
    {
      to?: string
      isOpen: boolean
      showCloseIcon?: boolean
    } & HTMLAttributeClass
  >(),
  {
    to: 'body',
    isOpen: false,
    showCloseIcon: true,
  }
)

const target = ref()

onMounted(() => {
  window.addEventListener('keyup', escPressed)
})

onUnmounted(() => {
  window.removeEventListener('keyup', escPressed)
})

const emits = defineEmits<{
  close: [event: MouseEvent | KeyboardEvent]
}>()

const isModalOpen = computed(() => props.isOpen)

const onClose = (event: MouseEvent | KeyboardEvent) => {
  emits('close', event)
}

const escPressed = (event: KeyboardEvent) => {
  if (event.code === 'Escape') {
    onClose(event)
  }
}

useBlockScroll(isModalOpen)
onClickOutside(target, onClose)
</script>

<template>
  <Teleport
    :to="to"
    :disabled="!props.isOpen"
  >
    <Transition name="backdrop">
      <div
        v-if="props.isOpen"
        class="z-modal-backdrop fixed left-0 top-0 !h-[100dvh] h-screen w-screen bg-black opacity-60"
      />
    </Transition>
    <Transition name="modal">
      <div
        v-if="props.isOpen"
        class="z-modal fixed left-0 top-0 flex !h-[100dvh] h-screen w-screen items-end justify-center md:items-center"
      >
        <div
          v-if="props.isOpen"
          ref="target"
          :class="[props.class, 'w-full max-w-[1055px] bg-white']"
        >
          <div class="flex min-h-20 items-center pl-5 md:min-h-24 md:pl-10">
            <slot name="header" />
            <button
              v-if="showCloseIcon"
              class="w-13.75 ml-auto flex max-h-10 items-center justify-center md:mr-3"
              @click="onClose"
            >
              <CloseCircleIcon class="size-8" />
            </button>
          </div>

          <div>
            <slot name="content" />
          </div>

          <div>
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.modal-enter-active {
  transition: all 200ms ease-out;
}

.modal-leave-active {
  transition: all 200ms ease-out;

  @media (min-width: theme('screens.md')) {
    transition: all 0ms ease-out;
  }
}

.modal-enter-from,
.modal-leave-to {
  transform: translateY(100%);
  @media (min-width: theme('screens.md')) {
    transform: translateY(0);
  }
}

.backdrop-enter-active {
  transition: all 220ms ease-out;
}

.backdrop-leave-active {
  transition: all 220ms ease-out;

  @media (min-width: theme('screens.md')) {
    transition: all 80ms ease-out;
  }
}

.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}
</style>
